<template>
  <div
    v-if="url"
    class="my-6 flex flex-col gap-[4px] items-center"
    :data-blok-c="dataBlokC"
    :data-blok-uid="dataBlokUid"
  >
    <div
      class="w-full rounded-[10px] overflow-hidden"
      :class="embedWrapperClasses"
      :style="wrapperStyle"
    >
      <iframe
        v-if="isDirectIframe"
        :src="iframeSrc"
        class="w-full border-0"
        :class="embedClasses"
        :style="iframeStyle"
        frameborder="0"
        allow="
          accelerometer;
          autoplay;
          clipboard-write;
          encrypted-media;
          gyroscope;
          picture-in-picture;
        "
        allowfullscreen
      ></iframe>
      <div v-else ref="embedContainer" :class="embedClasses"></div>
    </div>
    <p
      v-if="caption"
      class="text-[0.75rem] leading-[1rem] text-[#353535] text-center tracking-[-0.12px] w-full"
    >
      {{ caption }}
    </p>
  </div>
</template>

<script>
export default {
  name: 'ContentEmbed',
  props: {
    url: {
      type: String,
      default: '',
    },
    caption: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    aspectRatio: {
      type: String,
      default: 'auto',
      validator: value => ['square', 'wide', 'tall', 'auto'].includes(value),
    },
    dataBlokC: {
      type: String,
      default: '',
    },
    dataBlokUid: {
      type: String,
      default: '',
    },
  },
  computed: {
    trimmedUrl() {
      return this.url?.trim() || '';
    },
    embedWrapperClasses() {
      return 'bg-[#F5F5F5]';
    },
    wrapperStyle() {
      return {};
    },
    isTiktok() {
      return /tiktok\.com\/@[\w.-]+\/video\/\d+/.test(this.trimmedUrl);
    },
    isInstagram() {
      return /instagram\.com\/(p|reel|tv)/.test(this.trimmedUrl);
    },
    isYouTube() {
      return /(?:youtube\.com|youtu\.be)/.test(this.trimmedUrl);
    },
    isSpotify() {
      // Only treat a bare URL as Spotify; a pasted <iframe> goes through the
      // existing iframe-src extraction path instead.
      if (this.trimmedUrl.startsWith('<')) return false;
      return /open\.spotify\.com\/(?:embed\/)?(?:intl-[a-z]+\/)?(?:track|episode|show|playlist|album|artist)\//.test(
        this.trimmedUrl,
      );
    },
    youtubeVideoId() {
      if (!this.isYouTube) return null;

      const url = this.trimmedUrl;
      let extractedId = null;

      if (url.includes('youtube.com/watch')) {
        extractedId = new URL(url).searchParams.get('v');
      } else if (url.includes('youtu.be/')) {
        extractedId = url.split('youtu.be/')[1]?.split('?')[0];
      } else if (url.includes('/embed/')) {
        const embedMatch = url.match(/\/embed\/([^?&#]+)/);
        extractedId = embedMatch?.[1];
      } else if (url.includes('v=')) {
        const match = url.match(/[?&]v=([^?&#]+)/);
        extractedId = match?.[1];
      }

      return extractedId;
    },
    proxyVideoUrl() {
      const videoId = this.youtubeVideoId;
      if (!videoId) return '';

      const params = new URLSearchParams({
        v: videoId,
        autoplay: '0',
      });
      const origin = process.env.VUE_APP_APP_DOMAIN || window.location.origin;

      return `${origin}/youtube?${params.toString()}`;
    },
    detectedAspectRatio() {
      if (!this.trimmedUrl) return 'wide';

      if (this.isInstagram) return 'tall';
      if (this.isYouTube) return 'wide';
      if (this.isSpotify) return 'square';
      if (/(?:twitter\.com|x\.com)\/\w+\/status/.test(this.trimmedUrl)) return 'tall';
      if (/facebook\.com/.test(this.trimmedUrl)) return 'wide';

      return 'wide';
    },
    effectiveAspectRatio() {
      if (this.aspectRatio && this.aspectRatio !== 'auto') {
        return this.aspectRatio;
      }
      return this.detectedAspectRatio;
    },
    embedClasses() {
      if (this.isInstagram || this.isTiktok) {
        return 'w-full aspect-[9/16]';
      }

      const aspectMap = {
        square: 'aspect-square',
        wide: 'aspect-video',
        tall: 'aspect-[3/4]',
      };
      return aspectMap[this.effectiveAspectRatio] || aspectMap.wide;
    },
    iframeStyle() {
      if (this.isInstagram) {
        return {
          maxWidth: '540px',
          minWidth: '326px',
        };
      }
      return {};
    },
    processedEmbedCode() {
      if (!this.url) return '';
      return this.convertUrlToEmbedCode(this.trimmedUrl);
    },
    isDirectIframe() {
      if (!this.url) return false;

      if (this.isYouTube && this.proxyVideoUrl) return true;
      if (!this.processedEmbedCode) return false;

      if (this.processedEmbedCode.trim().startsWith('<iframe')) return true;
      if (this.isInstagram) return true;

      const trimmedUrl = this.trimmedUrl;
      const isTwitter = /(?:twitter\.com|x\.com)\/\w+\/status/.test(trimmedUrl);
      const isFacebook = /facebook\.com/.test(trimmedUrl);

      return !isTwitter && !isFacebook && trimmedUrl.startsWith('http');
    },
    iframeSrc() {
      if (!this.isDirectIframe) return '';

      if (this.isYouTube && this.proxyVideoUrl) {
        return this.proxyVideoUrl;
      }

      if (this.isInstagram) {
        const match = this.trimmedUrl.match(/instagram\.com\/(p|reel|tv)\/([A-Za-z0-9_-]+)/);
        if (match) {
          return `https://www.instagram.com/${match[1]}/${match[2]}/embed/`;
        }
      }

      if (this.isSpotify) {
        return this.processedEmbedCode;
      }

      if (this.processedEmbedCode.trim().startsWith('<iframe')) {
        const srcMatch = this.processedEmbedCode.match(/src=['"]([^'"]+)['"]/);
        if (srcMatch?.[1]) {
          return srcMatch[1];
        }
      }

      return this.url;
    },
  },
  mounted() {
    if (this.processedEmbedCode && this.$refs.embedContainer && !this.isDirectIframe) {
      this.loadEmbedCode();
    }
  },
  watch: {
    url() {
      if (this.processedEmbedCode && this.$refs.embedContainer && !this.isDirectIframe) {
        this.$nextTick(() => {
          this.loadEmbedCode();
        });
      }
    },
  },
  methods: {
    convertUrlToEmbedCode(url) {
      const instagramMatch = url.match(/instagram\.com\/(p|reel|tv)\/([A-Za-z0-9_-]+)/);
      if (instagramMatch) {
        const embedUrl = `https://www.instagram.com/${instagramMatch[1]}/${instagramMatch[2]}/embed/`;
        return `<iframe src="${embedUrl}" width="100%" height="800" style="max-width: 540px; min-width: 326px; width: 100%; border: 0; min-height: 600px;" scrolling="no" allowtransparency="true" allow="encrypted-media" allowfullscreen title="Instagram"></iframe>`;
      }

      const youtubeMatch = url.match(
        /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/,
      );
      if (youtubeMatch) {
        return '';
      }

      const twitterMatch = url.match(/(?:twitter\.com|x\.com)\/(\w+)\/status\/(\d+)/);
      if (twitterMatch) {
        const scriptOpen = '<' + 'script';
        const scriptClose = '</' + 'script>';
        return `<blockquote class="twitter-tweet"><a href="${url}"></a></blockquote>${scriptOpen} async src="https://platform.twitter.com/widgets.js" charset="utf-8"${scriptClose}`;
      }

      const facebookMatch = url.match(/facebook\.com\/(\w+)\/posts\/(\d+)/);
      if (facebookMatch) {
        return `<iframe src="https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(
          url,
        )}&width=500" width="500" height="500" style="border:none;overflow:hidden" scrolling="no" frameborder="0" allowfullscreen="true" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"></iframe>`;
      }

      const spotifyMatch = !url.trim().startsWith('<')
        ? url.match(
            /open\.spotify\.com\/(?:embed\/)?(?:intl-[a-z]+\/)?(track|episode|show|playlist|album|artist)\/([A-Za-z0-9]+)/,
          )
        : null;
      if (spotifyMatch) {
        return `https://open.spotify.com/embed/${spotifyMatch[1]}/${spotifyMatch[2]}`;
      }

      const tiktokMatch = url.match(/tiktok\.com\/@[\w.-]+\/video\/(\d+)/);
      if (tiktokMatch) {
        const params = new URLSearchParams({
          autoplay: '0',
          controls: '1',
          description: '0',
          rel: '0',
        }).toString();
        const embedUrl = `https://www.tiktok.com/player/v1/${tiktokMatch[1]}?${params}`;
        return `<iframe src="${embedUrl}" width="100%" height="580" style="max-width: 700px; min-width: 300px; width: 100%; border: 0;" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen title="TikTok"></iframe>`;
      }

      return url;
    },
    loadEmbedCode() {
      if (!this.$refs.embedContainer || !this.processedEmbedCode) return;

      const { htmlWithoutScripts, scripts } = this.extractScripts();

      this.$refs.embedContainer.innerHTML = htmlWithoutScripts;

      scripts.forEach(script => this.loadScript(script));

      setTimeout(this.processInstagramEmbeds, 500);
    },
    extractScripts() {
      const scriptOpen = '<' + 'script';
      const scriptClose = '</' + 'script>';
      const scriptRegex = new RegExp(scriptOpen + '[^>]*>([\\s\\S]*?)' + scriptClose, 'gi');

      const scripts = [];
      let htmlWithoutScripts = this.processedEmbedCode;
      let match;

      while ((match = scriptRegex.exec(this.processedEmbedCode)) !== null) {
        const rawSrc = match[0].match(/src=["']([^"']+)["']/)?.[1] || '';
        const normalizedSrc = this.normalizeInstagramScriptSrc(rawSrc);

        scripts.push({
          src: normalizedSrc,
          content: match[1],
          fullScript: match[0],
        });
        htmlWithoutScripts = htmlWithoutScripts.replace(match[0], '');
      }

      return { htmlWithoutScripts, scripts };
    },
    normalizeInstagramScriptSrc(src) {
      return src.includes('instagram.com/embed.js') ? 'https://www.instagram.com/embed.js' : src;
    },
    loadScript(script) {
      if (script.src) {
        this.loadExternalScript(script.src);
      } else if (script.content) {
        this.loadInlineScript(script.content);
      }
    },
    loadExternalScript(src) {
      const existingScript = document.querySelector(`script[src="${src}"]`);

      if (!existingScript) {
        const scriptElement = document.createElement('script');
        scriptElement.src = src;
        scriptElement.async = true;

        if (src.includes('instagram.com/embed.js')) {
          scriptElement.onload = this.processInstagramEmbeds;
        }

        document.body.appendChild(scriptElement);
      } else if (src.includes('instagram.com/embed.js')) {
        setTimeout(this.processInstagramEmbeds, 100);
      }
    },
    loadInlineScript(content) {
      const scriptElement = document.createElement('script');
      scriptElement.textContent = content;
      document.body.appendChild(scriptElement);
    },
    processInstagramEmbeds() {
      if (window.instgrm?.Embeds) {
        window.instgrm.Embeds.process();
      }
    },
  },
};
</script>

<style scoped>
* {
  font-family: 'Inter', 'aktiv-grotesk', sans-serif;
}
</style>
